import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// ─── safely read localStorage ────────────────────────────────────────────────
function readLocalUser() {
  try {
    const token = localStorage.getItem('authToken');
    const raw   = localStorage.getItem('user');
    if (token && raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }
  return null;
}

function clearLocalAuth() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('referral_code');
}

export const AuthProvider = ({ children }) => {
  // Read localStorage synchronously so UI never flashes "logged out" on reload
  const [user,          setUser]          = useState(() => readLocalUser());
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);

  // ✅ CRITICAL: starts false — Profile.jsx renders nothing until this is true
  // This prevents the race condition on mobile where the redirect fires before
  // we know whether the stored token is still valid on the server
  const [isInitialized, setIsInitialized] = useState(false);

  // ─── On every cold app load: validate the stored token against the server ──
  // If the jti was rotated (e.g. user logged out on another device), or the
  // token expired, the server returns 401 → we clear everything silently.
  // User never needs to manually clear browser data again.
  useEffect(() => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      // No token stored → nothing to validate, initialize immediately
      setIsInitialized(true);
      return;
    }

    api.get('/users/profile')
      .then((res) => {
        // Token is valid → refresh user object from server
        // (picks up any server-side changes like role updates, coin balance etc.)
        const freshUser = res.data;
        localStorage.setItem('user', JSON.stringify(freshUser));
        setUser(freshUser);
        setIsInitialized(true);
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          // Token is stale/expired/jti-mismatch → clear silently
          clearLocalAuth();
          setUser(null);
        }
        // On network errors (offline), keep the cached user so the app still
        // partially works — isInitialized still becomes true
        setIsInitialized(true);
      });
  }, []); // runs once on mount only

  // ─── Listen for auth:logout events fired by the api.js interceptor ───────
  // This handles 401s that happen mid-session (e.g. token rotated on backend)
  // without doing a hard window.location redirect that breaks React state
  useEffect(() => {
    const handleForceLogout = () => {
      clearLocalAuth();
      setUser(null);
    };
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  // ─── updateUser: always keep localStorage in sync ────────────────────────
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    if (updatedUser) {
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } else {
      clearLocalAuth();
    }
  };

  // ─── SIGNUP ──────────────────────────────────────────────────────────────
  const signup = async (email, password, passwordConfirmation) => {
    try {
      setError(null);
      const response = await authAPI.signup(email, password, passwordConfirmation);
      const { user, token } = response.data;
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      return { success: true, user };
    } catch (err) {
      const errorMessage =
        err.response?.data?.errors?.join(', ') ||
        err.response?.data?.error ||
        'Signup failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // ─── LOGIN (email/password) ───────────────────────────────────────────────
  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login(email, password);
      const { user, token } = response.data;
      if (!token || !user) throw new Error('Invalid login response');
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      return { success: true, user };
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || 'Invalid email or password';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // ─── LOGOUT ──────────────────────────────────────────────────────────────
  const logout = async () => {
    try {
      // Backend rotates jti → old token is immediately invalid everywhere
      await authAPI.logout();
    } catch {
      // Even if the API call fails, clear local state
      console.warn('Logout API call failed — clearing local state anyway');
    }
    clearLocalAuth();
    setUser(null);
  };

  const value = {
    user,
    loading,
    error,
    isInitialized,
    signup,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};